import React, { useCallback, useEffect, useState } from "react";
import { Heart, X, CornerDownRight, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { commentTime } from "@/lib/date";
import { Avatar, Textarea, Spinner } from "@/components/peeps/ui";

function Composer({ value, onChange, onSubmit, posting, placeholder, accent, onColor, testIDPrefix }) {
  return (
    <div className="flex items-end gap-2">
      <Textarea
        testID={`${testIDPrefix}-input`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1"
        style={{ minHeight: 42, maxHeight: 110 }}
        rows={1}
      />
      <button
        data-testid={`${testIDPrefix}-submit`}
        onClick={onSubmit}
        disabled={!value.trim() || posting}
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform active:scale-90"
        style={{ background: accent, opacity: !value.trim() || posting ? 0.4 : 1 }}
      >
        {posting ? <Spinner style={{ borderTopColor: onColor }} /> : <ArrowUp size={18} color={onColor} />}
      </button>
    </div>
  );
}

function CommentRow({ c, isReply, onLike, onReply, onDelete, meId }) {
  return (
    <div data-testid={`comment-${c.id}`} className="flex gap-2.5 items-start">
      {isReply && <CornerDownRight size={14} color={colors.muted} className="mt-3 shrink-0" />}
      <Avatar name={c.author?.display_name} uri={c.author?.profile_photo_url} size={isReply ? 28 : 34} />
      <div className="flex-1 min-w-0">
        <div className="rounded-2xl px-3.5 py-2.5" style={{ background: colors.surfaceSecondary }}>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-display font-bold text-[13px]" style={{ color: colors.onSurface }}>
              {c.author?.id === meId ? "You" : (c.author?.display_name || "Someone")}
            </span>
            <span className="font-mono2 text-[10px]" style={{ color: colors.faint }}>{commentTime(c.created_at)}</span>
          </div>
          <p className="font-display text-[13.5px] leading-snug whitespace-pre-wrap break-words" style={{ color: colors.onSurfaceSecondary }}>{c.text}</p>
        </div>
        <div className="flex items-center gap-4 mt-1 ml-1">
          <button data-testid={`comment-like-${c.id}`} onClick={() => onLike(c)} className="flex items-center gap-1 transition-transform active:scale-90">
            <Heart size={13} color={c.liked ? colors.brand : colors.muted} fill={c.liked ? colors.brand : "none"} />
            {c.like_count > 0 && <span className="font-display text-[11.5px]" style={{ color: c.liked ? colors.brand : colors.muted }}>{c.like_count}</span>}
          </button>
          {!isReply && (
            <button data-testid={`comment-reply-${c.id}`} onClick={() => onReply(c)} className="font-display font-medium text-[11.5px]" style={{ color: colors.muted }}>Reply</button>
          )}
          {c.can_delete && (
            <button data-testid={`delete-comment-${c.id}`} onClick={() => onDelete(c)} className="flex items-center gap-1"><X size={13} color={colors.muted} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommentThread({ parentType, parentId, accent = colors.brand, onColor = "#fff", onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyPosting, setReplyPosting] = useState(false);

  const total = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const load = useCallback(async () => {
    try {
      const res = await api.listComments(parentType, parentId);
      setComments(res.comments || []);
      onCountChange?.(res.count || 0);
    } catch (e) {
      toast(e.message || "Couldn't load comments");
    } finally {
      setLoading(false);
    }
  }, [parentType, parentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      await api.addThreadComment(parentType, parentId, t);
      setText("");
      await load();
    } catch (e) {
      toast(e.message || "Couldn't post");
    } finally {
      setPosting(false);
    }
  };

  const postReply = async (parent) => {
    const t = replyText.trim();
    if (!t) return;
    setReplyPosting(true);
    try {
      await api.addThreadComment(parentType, parentId, t, parent.id);
      setReplyText("");
      setReplyTo(null);
      await load();
    } catch (e) {
      toast(e.message || "Couldn't reply");
    } finally {
      setReplyPosting(false);
    }
  };

  const like = async (c) => {
    const patch = (arr) => arr.map((x) => {
      if (x.id === c.id) return { ...x, liked: !x.liked, like_count: x.like_count + (x.liked ? -1 : 1) };
      if (x.replies?.length) return { ...x, replies: patch(x.replies) };
      return x;
    });
    setComments((prev) => patch(prev));
    try { await api.likeComment(c.id); } catch { load(); }
  };

  const remove = async (c) => {
    setComments((prev) => prev.filter((x) => x.id !== c.id).map((x) => ({ ...x, replies: (x.replies || []).filter((r) => r.id !== c.id) })));
    try { await api.deleteComment(c.id); onCountChange?.(Math.max(0, total - 1)); } catch { load(); }
  };

  return (
    <div data-testid={`comment-thread-${parentType}`}>
      <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-3" style={{ color: colors.muted }}>
        Comments ({total})
      </p>

      <div className="mb-4">
        <Composer value={text} onChange={setText} onSubmit={post} posting={posting} placeholder="Add a comment…" accent={accent} onColor={onColor} testIDPrefix="comment" />
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : comments.length === 0 ? (
        <p className="font-display text-[13.5px]" style={{ color: colors.faint }}>No comments yet. Start the conversation.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {comments.map((c) => (
            <div key={c.id}>
              <CommentRow c={c} isReply={false} meId={user?.id} onLike={like} onReply={(cm) => { setReplyTo(replyTo === cm.id ? null : cm.id); setReplyText(""); }} onDelete={remove} />
              {(c.replies || []).length > 0 && (
                <div className="mt-3 ml-6 flex flex-col gap-3">
                  {c.replies.map((r) => (
                    <CommentRow key={r.id} c={r} isReply meId={user?.id} onLike={like} onReply={() => {}} onDelete={remove} />
                  ))}
                </div>
              )}
              {replyTo === c.id && (
                <div className="mt-2 ml-11">
                  <Composer value={replyText} onChange={setReplyText} onSubmit={() => postReply(c)} posting={replyPosting} placeholder={`Reply to ${c.author?.display_name || "them"}…`} accent={accent} onColor={onColor} testIDPrefix={`reply-${c.id}`} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
